import {
  Fragment,
  useState,
  useEffect,
} from 'react';
import classNames from 'classnames';
import {
  usePageContext,
} from 'vike-react/usePageContext';
import InstallButton from '@/components/InstallButton';
import GetInTouchModal from '@/components/Modals/Feedback/GetInTouchModal';
import RequestFeatureModal from '@/components/Modals/Feedback/RequestFeatureModal';
import RequestStoreModal from '@/components/Modals/Feedback/RequestStoreModal';
import extension from '@/utils/extension';
import useModal from '@/hooks/useModal';
import Button from '@/ui/components/Button';
import Text from '@/ui/components/Text';

import styles from './Footer.module.css';

type FooterProps = {
  kind?: 'default' | 'fixed';
  className?: string;
  withInstall?: boolean;
};

const Footer = (props: FooterProps) => {
  // ##########################################################################
  // PARAMS
  // #########################################################################

  const {
    platform,
  } = usePageContext();

  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    kind,
    className,
    withInstall,
  } = props;

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [isExtensionInstalled, setExtensionInstalled] = useState(true);

  const getInTouchModal = useModal();
  const requestStoreModal = useModal();
  const requestFeatureModal = useModal();

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const year = new Date().getFullYear();
  const withCTA = !isExtensionInstalled || ['ios', 'android'].includes(platform);

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    (async () => {
      const isExtensionInstalled = await extension.isInstalled();
      setExtensionInstalled(isExtensionInstalled);
    })();
  }, []);

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const footerClassName = classNames({
    [styles.footer]: true,
    [styles[kind]]: !!kind,
    [className]: !!className,
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Fragment>
      <GetInTouchModal {...getInTouchModal} />
      <RequestStoreModal {...requestStoreModal} />
      <RequestFeatureModal {...requestFeatureModal} />

      <div className={footerClassName}>
        {withInstall && withCTA && (
          <div className={styles.cta}>
            <div className={styles.ctaText}>
              <div className={styles.title}>
                <Text kind="super">
                  Start saving now
                </Text>
              </div>

              <div className={styles.description}>
                <Text
                  kind="heading-6"
                  color="neutral-500"
                  weight="400"
                >
                  Both on products and discounts...
                  Don’t make the dino wait!
                </Text>
              </div>
            </div>

            <div className={styles.ctaButton}>
              <InstallButton kind="inline" />
            </div>
          </div>
        )}
        <div className={styles.container}>
          <div className={styles.assets}>
            <a
              href="/"
              className={styles.logo}
            >
              <img
                src="/images/logo/with-text.png"
                alt="Dinosave"
              />
            </a>

            <div className={styles.social}>
              <a
                href="https://twitter.com/dinosave_"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/images/socials/x.svg"
                  alt="twitter"
                />
              </a>
              <a
                href="https://www.instagram.com/dinosave.co"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/images/socials/instagram.svg"
                  alt="instagram"
                />
              </a>
            </div>
          </div>

          <div className={styles.links}>
            <div className={styles.link}>
              <Button
                kind="link"
                color="secondary"
                onClick={requestStoreModal.toggle}
              >
                <Text
                  kind="control"
                  size="large"
                >
                  Request a store
                </Text>
              </Button>
            </div>

            <div className={styles.link}>
              <Button
                kind="link"
                color="secondary"
                onClick={requestFeatureModal.toggle}
              >
                <Text
                  kind="control"
                  size="large"
                >
                  Request a feature
                </Text>
              </Button>
            </div>

            <div className={styles.link}>
              <Button
                kind="link"
                color="secondary"
                onClick={getInTouchModal.toggle}
              >
                <Text
                  kind="control"
                  size="large"
                >
                  Get in touch
                </Text>
              </Button>
            </div>
          </div>

          <div className={styles.basement}>
            <Text
              kind="caption"
              size="large"
              color="neutral-500"
            >
              dinosave ©️ {year}
            </Text>

            <div className={styles.links}>
              <a href="/privacy">
                <Text
                  kind="caption"
                  size="large"
                  color="neutral-500"
                >
                  privacy policy
                </Text>
              </a>

              <a href="/terms">
                <Text
                  kind="caption"
                  size="large"
                  color="neutral-500"
                >
                  terms of service
                </Text>
              </a>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Footer;
